import { Classes, Icon } from '@clarion-blueprint-shim';
import { RefObject } from 'react';
import {
  composeRenderProps,
  Link as RACLink,
  LinkProps as RACLinkProps,
  TooltipTrigger,
} from 'react-aria-components';
import { cx } from '../util';
import { buttonStyles, ClarionButtonOrLinkProps } from './ClarionButton';
import { ClarionSpinner } from './ClarionSpinner';
import { ClarionTooltip } from './ClarionTooltip';

export interface ClarionButtonLinkProps extends ClarionButtonOrLinkProps, RACLinkProps {
  ref?: RefObject<HTMLAnchorElement>;
}

// Wrapper around RAC Link with a Blueprint-like API and styles
export const ClarionButtonLink = ({
  ref,
  icon,
  endIcon,
  children,
  tooltip,
  tooltipPlacement = 'bottom',
  ...props
}: ClarionButtonLinkProps) => {
  const link = (
    <RACLink
      ref={ref}
      // If tooltip is provided, use it as the aria-label (will be overridden by explicit aria-label in the ...props spread below)
      aria-label={tooltip}
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        buttonStyles({ ...renderProps, ...props, className }),
      )}
      {...(props.variant === 'selected' ? { 'aria-current': 'page' } : {})}
    >
      {composeRenderProps(children, (children) => (
        <>
          {props.loading ? <ClarionSpinner size={16} /> : icon && <Icon icon={icon} />}
          {children && <span className={cx(Classes.BUTTON_TEXT, 'min-w-0')}>{children}</span>}
          {endIcon && <Icon icon={endIcon} />}
        </>
      ))}
    </RACLink>
  );

  if (tooltip) {
    return (
      <TooltipTrigger delay={500}>
        {link}
        <ClarionTooltip placement={tooltipPlacement}>{tooltip}</ClarionTooltip>
      </TooltipTrigger>
    );
  }

  return link;
};
